import { useEffect } from "react";
import { forceUnlockScroll, isScrollLocked } from "../../utils/scrollLock";

/*
 * Safety net for a "stuck" page: the page can't scroll although nothing is on screen to explain it.
 * Every modal now goes through utils/scrollLock, but third-party libraries and any lock added later can still
 * leave `overflow: hidden` behind (for example after an idle tab, or a modal that unmounts mid-animation).
 *
 * It only acts when ALL of these hold, so it can never unlock a page behind a real modal:
 *   - the page is locked (body or html is overflow: hidden, or Bootstrap's modal-open class is set)
 *   - nobody holds the shared lock, and no Bootstrap modal is showing
 *   - no large visible overlay covers the middle of the screen
 */

const isLockedStyle = () =>
  document.body.style.overflow === "hidden" ||
  document.documentElement.style.overflow === "hidden" ||
  document.body.classList.contains("modal-open");

// A visible, clickable, fixed-position layer that covers a big part of the viewport = a real modal / drawer / lightbox
const hasBigOverlay = () => {
  const vw = window.innerWidth;
  const vh = window.innerHeight;
  const points = [
    [vw * 0.5, vh * 0.5],
    [vw * 0.5, vh * 0.2],
    [vw * 0.5, vh * 0.8],
    [vw * 0.85, vh * 0.5],
  ];
  for (const [x, y] of points) {
    for (const el of document.elementsFromPoint(x, y)) {
      if (el === document.body || el === document.documentElement) continue;
      const cs = window.getComputedStyle(el);
      if (cs.position !== "fixed") continue;
      if (cs.visibility === "hidden" || cs.pointerEvents === "none" || Number(cs.opacity) < 0.05) continue;
      const r = el.getBoundingClientRect();
      if (r.width * r.height >= vw * vh * 0.35) return true;
    }
  }
  return false;
};

const ScrollGuard = () => {
  useEffect(() => {
    const check = () => {
      if (document.hidden || !isLockedStyle()) return;
      if (isScrollLocked()) return; // a modal holds the shared lock: legitimate
      if (document.querySelector(".modal.show, .modal-backdrop")) return; // Bootstrap's own modal manager
      if (hasBigOverlay()) return;

      forceUnlockScroll();
      document.body.style.overflow = "";
      document.body.style.paddingRight = "";
      document.documentElement.style.overflow = "";
      document.body.classList.remove("modal-open");
    };

    const events = ["wheel", "touchstart", "keydown", "focus", "visibilitychange"];
    events.forEach((e) => window.addEventListener(e, check, { passive: true }));
    document.addEventListener("visibilitychange", check);
    const timer = setInterval(check, 2000);
    return () => {
      events.forEach((e) => window.removeEventListener(e, check));
      document.removeEventListener("visibilitychange", check);
      clearInterval(timer);
    };
  }, []);

  return null;
};

export default ScrollGuard;
